import { cn, Tooltip } from '@frontend-platform/ui-lib';
import { Button } from '@frontend-platform/ui-lib/ui';
import { type ReactNode, useState } from 'react';
import { readStoredDevBot } from './dev-bot-storage';
import { pushDevSession } from './dev-session';

const BOT_KEY = 'currentBot';

const CONTROL_FRAME =
  'flex h-9 items-center gap-1.5 rounded-md border border-steel-blue-45 bg-white px-2.5 font-medium text-xs';

/**
 * A dev-setup control the app has not wired up yet. It stays visible, greyed out, with a
 * tooltip that says what to add, so every module shows the same capabilities.
 */
export function DevShellDisabledControl({ hint, children }: { hint: string; children: ReactNode }) {
  return (
    <Tooltip title={hint}>
      <span
        role="note"
        aria-disabled="true"
        aria-label={hint}
        className={cn(CONTROL_FRAME, 'cursor-not-allowed text-steel-ultralight opacity-60')}
      >
        {children}
      </span>
    </Tooltip>
  );
}

export type EnvProductType = 'STATIC_ENV' | 'DYNAMIC_ENV';

export interface DevShellEnvToggleConfig {
  /** Called after `currentBot.envProductType` is written and before the reload. */
  onChange?: (next: EnvProductType) => void;
}

const ENV_MODES: ReadonlyArray<{ value: EnvProductType; label: string }> = [
  { value: 'STATIC_ENV', label: 'Non-EP' },
  { value: 'DYNAMIC_ENV', label: 'EP' },
];

function readEnvProductType(): EnvProductType {
  return readStoredDevBot().envProductType === 'DYNAMIC_ENV' ? 'DYNAMIC_ENV' : 'STATIC_ENV';
}

export function DevShellEnvToggle({ config }: { config?: DevShellEnvToggleConfig }) {
  if (!config) {
    return (
      <DevShellDisabledControl hint="EP toggle not wired: this app does not read currentBot.envProductType yet. Pass config.envToggle once it does.">
        Non-EP / EP
      </DevShellDisabledControl>
    );
  }

  const current = readEnvProductType();

  async function select(next: EnvProductType) {
    try {
      localStorage.setItem(
        BOT_KEY,
        JSON.stringify({ ...readStoredDevBot(), envProductType: next })
      );
    } catch {
      // The reload re-reads the previous value when storage is unavailable.
    }
    config?.onChange?.(next);
    await pushDevSession();
    window.location.reload();
  }

  return (
    <Tooltip title="Switch the dev AI Agent between Non-EP (STATIC_ENV) and EP (DYNAMIC_ENV), then reload">
      <div className="flex items-center gap-0.5 rounded-md border border-steel-blue-45 bg-steel-blue-5 p-0.5">
        {ENV_MODES.map((mode) => {
          const active = current === mode.value;
          return (
            <Button
              key={mode.value}
              type="button"
              variant={active ? 'default' : 'ghost'}
              size="sm"
              aria-pressed={active}
              onClick={() => {
                if (!active) select(mode.value);
              }}
              className={cn('h-8 px-2.5 font-medium text-xs', !active && 'text-steel-light')}
            >
              {mode.label}
            </Button>
          );
        })}
      </div>
    </Tooltip>
  );
}

export interface DevShellMockApi {
  isEnabled: () => boolean;
  setEnabled: (next: boolean) => void;
}

export function DevShellMockToggle({
  mockApi,
  onChange,
}: {
  mockApi?: DevShellMockApi;
  onChange?: (enabled: boolean) => void;
}) {
  const [isMock, setIsMock] = useState(() => mockApi?.isEnabled() ?? false);

  if (!mockApi) {
    return (
      <DevShellDisabledControl hint="Mock toggle not wired: add a mock/real API dispatcher (AGENTS.md, App patterns) and pass config.mockApi.">
        <span className="block size-1.5 rounded-full bg-steel-blue-45" aria-hidden />
        Mock
      </DevShellDisabledControl>
    );
  }

  function toggle() {
    const next = !isMock;
    mockApi?.setEnabled(next);
    setIsMock(next);
    onChange?.(next);
  }

  return (
    <Tooltip
      title={
        isMock
          ? 'Serving mocked APIs. Click to use the real backend.'
          : 'Using the real backend. Click to serve mocked APIs.'
      }
    >
      <button
        type="button"
        onClick={toggle}
        aria-pressed={isMock}
        className={cn(
          CONTROL_FRAME,
          'transition-colors hover:bg-steel-blue-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30'
        )}
      >
        <span
          className={cn(
            'block size-1.5 rounded-full',
            isMock ? 'bg-violet-500' : 'bg-steel-blue-45'
          )}
          aria-hidden
        />
        <span className="text-steel-light">Mock</span>
        <span className="text-steel-ultradark">{isMock ? 'ON' : 'OFF'}</span>
      </button>
    </Tooltip>
  );
}
